'use client';

import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight, CheckCircle, BarChart3, Zap, Stethoscope, Brain, HeartPulse, Microscope, FileSearch, Activity, AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function HealthtechPage() {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const solutions = [
    {
      title: "AI-Powered Diagnostic Assistant",
      icon: Brain,
      description: "Advanced machine learning algorithms that analyze medical images, lab results, and patient data to provide diagnostic support and clinical decision recommendations.",
      benefits: [
        "92% increase in early disease detection",
        "3x faster diagnosis for critical conditions",
        "45% reduction in diagnostic errors",
        "Integration with existing medical imaging systems"
      ]
    },
    {
      title: "Remote Patient Monitoring",
      icon: HeartPulse,
      description: "IoT-enabled continuous monitoring platform that tracks vital signs, medication adherence, and activity levels for chronic disease management.",
      benefits: [
        "63% reduction in hospital readmissions", 
        "24/7 real-time health status monitoring",
        "Automated clinical alerts based on personalized thresholds",
        "Secure patient data transmission and storage"
      ]
    },
    {
      title: "Precision Medicine Platform",
      icon: Microscope,
      description: "Genomic analysis and personalized treatment planning tools that enable tailored therapeutic approaches based on individual genetic profiles.",
      benefits: [
        "78% improvement in treatment efficacy for targeted conditions",
        "Reduced adverse medication reactions by 52%", 
        "Integration of genomic data with electronic health records",
        "Clinical trial matching based on genetic markers"
      ]
    },
    {
      title: "Healthcare Analytics Suite",
      icon: BarChart3,
      description: "Comprehensive data analytics platform that transforms healthcare operations data into actionable insights for improved efficiency and patient outcomes.",
      benefits: [
        "35% reduction in operational costs", 
        "Predictive resource allocation for peak demand periods",
        "Quality of care metrics with benchmarking capabilities",
        "Population health risk stratification"
      ]
    },
    {
      title: "Interoperable Health Records",
      icon: FileSearch,
      description: "Blockchain-secured health information exchange that enables seamless sharing of patient data across providers while maintaining privacy and security.",
      benefits: [
        "Complete patient history available at point of care", 
        "80% reduction in duplicate medical tests", 
        "Patient-controlled access and consent management",
        "HIPAA and GDPR compliant data sharing framework"
      ]
    }
  ];

  const useCases = [
    {
      industry: "Hospitals",
      organization: "Metropolitan Medical Center",
      challenge: "Large hospital system struggling with diagnostic accuracy, lengthy wait times, and inefficient resource allocation.",
      solution: "Implemented our AI-Powered Diagnostic Assistant and Healthcare Analytics Suite to optimize clinical workflows and improve diagnostic precision.",
      results: "47% decrease in diagnostic turnaround time, 38% reduction in unnecessary tests, and $4.3M annual savings through operational efficiencies."
    },
    {
      industry: "Clinics",
      organization: "Primary Care Partners",
      challenge: "Network of primary care clinics facing challenges in managing chronic disease patients and preventing hospital readmissions.",
      solution: "Deployed our Remote Patient Monitoring solution integrated with Interoperable Health Records for comprehensive care coordination.",
      results: "72% reduction in ER visits for chronic patients, 68% improvement in medication adherence, and 93% patient satisfaction with remote care capabilities."
    },
    {
      industry: "Research",
      organization: "Global Therapeutics Institute",
      challenge: "Research organization seeking to accelerate drug discovery and improve clinical trial outcomes for targeted therapies.",
      solution: "Implemented our Precision Medicine Platform with genomic analysis and patient matching capabilities.",
      results: "35% faster identification of potential therapeutic targets, 58% improvement in clinical trial enrollment rates, and successful development of two precision therapeutics."
    }
  ];

  const technologies = [
    {
      name: "Deep Learning & Computer Vision",
      description: "Neural networks trained on millions of medical images to detect abnormalities with superhuman accuracy in radiology, pathology, and dermatology applications."
    },
    {
      name: "Internet of Medical Things",
      description: "Network of connected medical devices providing continuous monitoring of vital signs and health metrics with secure data transmission protocols."
    },
    {
      name: "Genomic Analysis",
      description: "High-throughput sequencing and bioinformatics platforms enabling rapid analysis of genetic data for personalized medicine applications and drug discovery."
    },
    {
      name: "Federated Learning",
      description: "Privacy-preserving AI training methodology that allows models to learn from data across multiple healthcare institutions without sharing sensitive patient information."
    },
    {
      name: "Natural Language Processing",
      description: "Advanced algorithms that extract structured information from clinical notes, medical literature, and patient communications to support clinical decision making."
    }
  ];

  return (
    <div className="container mx-auto max-w-7xl px-4 py-16 mt-16">
      {/* Hero Section */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        className="grid md:grid-cols-2 gap-12 items-center mb-20"
      >
        <div className="space-y-6">
          <Badge className="px-3 py-1 text-sm bg-green-100 text-green-700 hover:bg-green-200">
            Healthcare Technology
          </Badge>
          
          <h1 className="text-4xl font-bold tracking-tight md:text-5xl">
            AI-Powered HealthTech Solutions
          </h1>
          
          <p className="text-xl text-muted-foreground">
            Transform patient care with our cutting-edge healthcare technology solutions designed to improve clinical outcomes, enhance operational efficiency, and revolutionize the delivery of care.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <Button size="lg" variant="outline" asChild>
              <Link href="/services">
                Our Services
              </Link>
            </Button>
          </div>

          <div className="pt-4 flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <span className="text-sm">HIPAA and GDPR compliant</span>
          </div>
        </div>

        <div className="relative bg-gradient-to-br from-teal-100 via-green-50 to-teal-100 rounded-2xl p-8 shadow-lg overflow-hidden">
          <div className="absolute inset-0 bg-white/40 rounded-2xl backdrop-blur-sm" />
          {/* Interactive HealthTech Dashboard */}
          <div className="relative z-10 bg-white/95 backdrop-blur-md rounded-xl shadow-lg overflow-hidden">
            {/* Dashboard Header */}
            <div className="bg-gradient-to-r from-teal-500 to-green-500 p-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Stethoscope className="h-6 w-6 text-white" />
                  <h3 className="font-semibold text-white">HealthTech Operations Dashboard</h3>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="text-white border-white/30 bg-white/10">
                    Live Data
                  </Badge>
                  <span className="text-white/80 text-sm flex items-center">
                    <BarChart3 className="h-3.5 w-3.5 mr-1" />
                    Updated 2m ago
                  </span>
                </div>
              </div>
            </div>
            {/* Dashboard Tabs */}
            <div className="p-1 bg-gray-100/80">
              <Tabs defaultValue="overview" className="w-full">
                <TabsList className="grid grid-cols-4 h-8">
                  <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
                  <TabsTrigger value="patients" className="text-xs">Patients</TabsTrigger>
                  <TabsTrigger value="alerts" className="text-xs">Alerts</TabsTrigger>
                  <TabsTrigger value="analytics" className="text-xs">Analytics</TabsTrigger>
                </TabsList>
                {/* Overview Tab */}
                <TabsContent value="overview" className="p-3">
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    {/* Admissions */}
                    <Card className="bg-white/70 shadow-sm">
                      <CardHeader className="pb-2 pt-3">
                        <CardTitle className="text-sm font-medium">Admissions</CardTitle>
                      </CardHeader>
                      <CardContent className="pb-3">
                        <div className="flex justify-between items-center text-xs">
                          <span>Today</span>
                          <span className="font-medium text-green-700">+32</span>
                        </div>
                        <div className="flex justify-between items-center text-xs">
                          <span>This Month</span>
                          <span className="font-medium">1,204</span>
                        </div>
                      </CardContent>
                    </Card>
                    {/* Diagnostics */}
                    <Card className="bg-white/70 shadow-sm">
                      <CardHeader className="pb-2 pt-3">
                        <CardTitle className="text-sm font-medium">Diagnostics</CardTitle>
                      </CardHeader>
                      <CardContent className="pb-3">
                        <div className="flex justify-between items-center text-xs">
                          <span>Completed</span>
                          <span className="font-medium text-green-700">+87</span>
                        </div>
                        <div className="flex justify-between items-center text-xs">
                          <span>Pending</span>
                          <span className="font-medium">14</span>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  {/* Patient Vitals Chart */}
                  <Card className="bg-white/70 shadow-sm mb-3">
                    <CardHeader className="pb-1 pt-3">
                      <CardTitle className="text-sm font-medium">Patient Vitals (Avg.)</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 pb-2">
                      <div className="bg-gray-100 rounded-md overflow-hidden relative h-24 flex items-center justify-center">
                        <svg viewBox="0 0 100 30" className="w-full h-full stroke-teal-600 stroke-2 fill-teal-100/70 stroke-linecap-round stroke-linejoin-round">
                          <defs>
                            <linearGradient id="healthtech-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                              <stop offset="0%" stopColor="rgb(13,148,136)" stopOpacity="0.5" />
                              <stop offset="100%" stopColor="rgb(13,148,136)" stopOpacity="0" />
                            </linearGradient>
                          </defs>
                          <path d="M0,20 Q10,10 20,18 T40,12 T60,15 T80,10 T100,20 L100,30 H0 Z" fill="url(#healthtech-gradient)" />
                          <path d="M0,20 Q10,10 20,18 T40,12 T60,15 T80,10 T100,20" fill="none" />
                        </svg>
                        <div className="absolute bottom-1 right-1 text-xs bg-white/80 rounded px-1">Last 24h</div>
                      </div>
                    </CardContent>
                  </Card>
                  {/* Compliance Score */}
                  <Card className="bg-white/70 shadow-sm">
                    <CardHeader className="pb-1 pt-3">
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-sm font-medium">Compliance Score</CardTitle>
                        <Badge variant="outline" className="text-xs h-5 px-1.5 bg-green-100 text-green-800 hover:bg-green-200 border-green-200">
                          99/100
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0 pb-2">
                      <div className="space-y-1.5">
                        <div className="text-xs p-1.5 rounded bg-gray-100/80 flex items-center justify-between">
                          <span className="font-medium">Audits Passed</span>
                          <Badge variant="outline" className="text-xs h-5 px-1.5 border-green-200 text-green-800 bg-green-50">8</Badge>
                        </div>
                        <div className="text-xs p-1.5 rounded bg-gray-100/80 flex items-center justify-between">
                          <span className="font-medium">Pending Reviews</span>
                          <Badge variant="outline" className="text-xs h-5 px-1.5 border-teal-200 text-teal-800 bg-teal-50">1</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
                {/* Patients Tab */}
                <TabsContent value="patients" className="p-3">
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    {/* Patient Types */}
                    <Card className="bg-white/70 shadow-sm">
                      <CardHeader className="pb-2 pt-3">
                        <CardTitle className="text-sm font-medium">Patient Types</CardTitle>
                      </CardHeader>
                      <CardContent className="pb-3">
                        <div className="flex flex-col gap-2 text-xs">
                          <span>Inpatients: <span className="font-medium text-teal-600">320</span></span>
                          <span>Outpatients: <span className="font-medium text-green-600">1,120</span></span>
                          <span>ICU: <span className="font-medium text-red-600">18</span></span>
                        </div>
                      </CardContent>
                    </Card>
                    {/* Chronic Disease Monitoring */}
                    <Card className="bg-white/70 shadow-sm">
                      <CardHeader className="pb-2 pt-3">
                        <CardTitle className="text-sm font-medium">Chronic Disease Monitoring</CardTitle>
                      </CardHeader>
                      <CardContent className="pb-3">
                        <div className="space-y-2">
                          <div className="flex justify-between items-center text-xs">
                            <span>Active Cases</span>
                            <span className="font-medium">142</span>
                          </div>
                          <div className="flex justify-between items-center text-xs">
                            <span>Remote Monitoring</span>
                            <span className="font-medium text-teal-700">+38</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  {/* Medication Adherence */}
                  <Card className="bg-white/70 shadow-sm mb-3">
                    <CardHeader className="pb-1 pt-3">
                      <CardTitle className="text-sm font-medium">Medication Adherence</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 pb-2">
                      <div className="flex justify-between items-center text-xs">
                        <span>Adherence Rate</span>
                        <span className="font-medium text-green-700">94%</span>
                      </div>
                      <div className="flex justify-between items-center text-xs">
                        <span>Missed Doses</span>
                        <span className="font-medium text-red-600">6</span>
                      </div>
                    </CardContent>
                  </Card>
                  {/* Patient Satisfaction */}
                  <Card className="bg-white/70 shadow-sm">
                    <CardHeader className="pb-1 pt-3">
                      <CardTitle className="text-sm font-medium">Patient Satisfaction</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 pb-2">
                      <div className="flex items-center gap-2 text-xs">
                        <span className="font-medium">Score:</span>
                        <span className="font-bold text-teal-700">4.8/5</span>
                        <span className="text-muted-foreground">(last 30 days)</span>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
                {/* Alerts Tab */}
                <TabsContent value="alerts" className="p-3">
                  <div className="space-y-2">
                    <div className="bg-white border-l-4 border-teal-600 rounded shadow-sm p-3 flex items-start">
                      <Activity className="h-4 w-4 text-teal-600 flex-shrink-0 mt-0.5" />
                      <div className="ml-2">
                        <h4 className="text-sm font-medium">Critical Alert</h4>
                        <p className="text-xs text-muted-foreground">Patient in ICU requires immediate attention (Room 204).</p>
                        <div className="mt-1 text-xs flex items-center">
                          <ArrowRight className="h-3 w-3 mr-1" />
                          <span className="text-muted-foreground">2 minutes ago</span>
                          <Button variant="link" size="sm" className="h-auto p-0 ml-2 text-xs">
                            View Details
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white border-l-4 border-yellow-500 rounded shadow-sm p-3 flex items-start">
                      <AlertTriangle className="h-4 w-4 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <div className="ml-2">
                        <h4 className="text-sm font-medium">Compliance Warning</h4>
                        <p className="text-xs text-muted-foreground">HIPAA compliance review required for new data integration.</p>
                        <div className="mt-1 text-xs flex items-center">
                          <ArrowRight className="h-3 w-3 mr-1" />
                          <span className="text-muted-foreground">1 hour ago</span>
                          <Button variant="link" size="sm" className="h-auto p-0 ml-2 text-xs">
                            View Details
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white border-l-4 border-red-500 rounded shadow-sm p-3 flex items-start">
                      <AlertTriangle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                      <div className="ml-2">
                        <h4 className="text-sm font-medium">System Alert</h4>
                        <p className="text-xs text-muted-foreground">API latency spike detected in diagnostic platform.</p>
                        <div className="mt-1 text-xs flex items-center">
                          <ArrowRight className="h-3 w-3 mr-1" />
                          <span className="text-muted-foreground">Today</span>
                          <Button variant="link" size="sm" className="h-auto p-0 ml-2 text-xs">
                            Review Report
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>
                {/* Analytics Tab */}
                <TabsContent value="analytics" className="p-3">
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    {/* Operational KPIs */}
                    <Card className="bg-white/70 shadow-sm">
                      <CardHeader className="pb-2 pt-3">
                        <CardTitle className="text-sm font-medium">Operational KPIs</CardTitle>
                      </CardHeader>
                      <CardContent className="pb-3">
                        <div className="flex flex-col gap-2 text-xs">
                          <span>Avg. Length of Stay: <span className="font-medium text-teal-600">3.2 days</span></span>
                          <span>Bed Occupancy: <span className="font-medium text-green-600">87%</span></span>
                          <span>Readmission Rate: <span className="font-medium text-red-600">7.1%</span></span>
                        </div>
                      </CardContent>
                    </Card>
                    {/* Resource Utilization */}
                    <Card className="bg-white/70 shadow-sm">
                      <CardHeader className="pb-2 pt-3">
                        <CardTitle className="text-sm font-medium">Resource Utilization</CardTitle>
                      </CardHeader>
                      <CardContent className="pb-3">
                        <div className="flex flex-col gap-2 text-xs">
                          <span>ICU Beds: <span className="font-medium text-teal-600">16/18</span></span>
                          <span>Ventilators: <span className="font-medium text-green-600">12/15</span></span>
                          <span>Staff On Duty: <span className="font-medium text-teal-700">54</span></span>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  {/* Analytics Chart */}
                  <Card className="bg-white/70 shadow-sm mb-3">
                    <CardHeader className="pb-1 pt-3">
                      <CardTitle className="text-sm font-medium">Admissions & Discharges (Last 30 Days)</CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 pb-2">
                      <div className="bg-gray-100 rounded-md overflow-hidden relative h-24 flex items-center justify-center">
                        <svg viewBox="0 0 100 30" className="w-full h-full stroke-green-600 stroke-2 fill-green-100/70 stroke-linecap-round stroke-linejoin-round">
                          <defs>
                            <linearGradient id="admissions-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                              <stop offset="0%" stopColor="rgb(16,185,129)" stopOpacity="0.5" />
                              <stop offset="100%" stopColor="rgb(16,185,129)" stopOpacity="0" />
                            </linearGradient>
                          </defs>
                          <path d="M0,25 Q10,20 20,22 T40,15 T60,18 T80,12 T100,20 L100,30 H0 Z" fill="url(#admissions-gradient)" />
                          <path d="M0,25 Q10,20 20,22 T40,15 T60,18 T80,12 T100,20" fill="none" />
                        </svg>
                        <div className="absolute bottom-1 right-1 text-xs bg-white/80 rounded px-1">Last 30 days</div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
            {/* Dashboard Footer */}
            <div className="bg-gray-100/80 p-2 flex justify-between items-center text-xs text-muted-foreground">
              <div>Powered by OpenLabs AI</div>
              <div className="flex items-center">
                <Button variant="ghost" size="sm" className="h-6 text-xs gap-1">
                  <Zap className="h-3 w-3" /> Optimize Operations
                </Button>
                <Button variant="ghost" size="sm" className="h-6 text-xs gap-1">
                  <ArrowRight className="h-3 w-3" /> Full Dashboard
                </Button>
              </div>
            </div>
          </div>
          {/* Summary Metrics Row */}
          <div className="mt-6 grid grid-cols-3 gap-4 text-center">
            <div className="bg-white/80 backdrop-blur-sm p-3 rounded-lg">
              <p className="text-2xl font-bold text-teal-600">47%</p>
              <p className="text-sm text-gray-600">Better Outcomes</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-3 rounded-lg">
              <p className="text-2xl font-bold text-teal-600">63%</p>
              <p className="text-sm text-gray-600">Fewer Readmissions</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-3 rounded-lg">
              <p className="text-2xl font-bold text-teal-600">3.8x</p>
              <p className="text-sm text-gray-600">ROI</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Main Benefits */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={fadeIn}
        className="text-center mb-16"
      >
        <h2 className="text-3xl font-bold mb-12">Why Choose Our HealthTech Solutions?</h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-600 text-white w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <Stethoscope className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Clinical Excellence</h3>
            <p className="text-gray-600">
              Solutions developed in collaboration with leading medical professionals and validated through rigorous clinical studies for proven efficacy.
            </p>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-600 text-white w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <Activity className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Patient-Centered Design</h3>
            <p className="text-gray-600">
              Technology that puts patients at the center, improving engagement, satisfaction, and health outcomes through intuitive experiences.
            </p>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-600 text-white w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <Zap className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Operational Transformation</h3>
            <p className="text-gray-600">
              Intelligent automation and workflow optimization that reduce administrative burden, allowing healthcare providers to focus on patient care.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Solutions */}
      <motion.section 
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
        className="mb-20"
      >
        <h2 className="text-3xl font-bold mb-12 text-center">Our HealthTech Solutions</h2>
        
        <div className="grid gap-8">
          {solutions.map((solution, index) => (
            <motion.div 
              key={solution.title}
              variants={fadeIn}
              className={`flex flex-col md:flex-row gap-8 bg-white p-6 rounded-xl shadow-sm border border-gray-100 ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}
            >
              <div className="md:w-1/3 flex flex-col items-center md:items-start text-center md:text-left">
                <div className="bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-600 text-white w-16 h-16 rounded-xl flex items-center justify-center mb-4">
                  <solution.icon className="h-8 w-8" />
                </div>
                <h3 className="text-2xl font-semibold mb-2">{solution.title}</h3>
                <p className="text-gray-600 mb-4">{solution.description}</p>
              </div>
              
              <div className="md:w-2/3 bg-gray-50 rounded-xl p-6">
                <h4 className="font-semibold mb-4">Key Benefits:</h4>
                <div className="grid sm:grid-cols-2 gap-3">
                  {solution.benefits.map((benefit) => (
                    <div key={benefit} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm">{benefit}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Use Cases Tabs */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={fadeIn}
        className="mb-20"
      >
        <h2 className="text-3xl font-bold mb-4 text-center">Applicable Use Cases</h2>
        <p className="text-center text-muted-foreground max-w-3xl mx-auto mb-12">
          Our HealthTech solutions can be tailored for various healthcare applications, providing significant advantages across different medical sectors.
        </p>
        <Tabs defaultValue="hospitals" className="w-full">
          <TabsList className="w-full flex justify-center mb-8 gap-2">
            <TabsTrigger value="hospitals">Hospitals</TabsTrigger>
            <TabsTrigger value="clinics">Clinics</TabsTrigger>
            <TabsTrigger value="research">Research</TabsTrigger>
          </TabsList>
          {/* Hospitals Tab */}
          <TabsContent value="hospitals" className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="md:w-1/2">
                <Badge className="mb-3 bg-green-100 text-green-700 hover:bg-green-200">Hospitals</Badge>
                <h3 className="text-2xl font-semibold mb-4">AI-Driven Clinical Workflow Optimization</h3>
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold mb-1">Industry Challenge</h4>
                    <p className="text-gray-600">Hospitals face increasing demand for accurate diagnostics, reduced wait times, and efficient resource allocation while maintaining high standards of patient care and compliance.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Key Applications</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /><span>AI-powered diagnostic support for imaging and lab results</span></li>
                      <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /><span>Predictive analytics for patient flow and resource allocation</span></li>
                      <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /><span>Automated compliance and quality of care monitoring</span></li>
                      <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /><span>Real-time clinical alerts and workflow automation</span></li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Expected Benefits</h4>
                    <p className="text-gray-600">Potential outcomes include 40-50% faster diagnostics, 30-40% reduction in unnecessary tests, and improved patient throughput and satisfaction.</p>
                  </div>
                </div>
              </div>
              <div className="md:w-1/2 bg-gray-50 rounded-xl p-8">
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h4 className="font-medium text-lg mb-4 text-center">Technology Stack</h4>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="bg-green-100 p-2 rounded-md">
                        <Brain className="h-5 w-5 text-green-700" />
                      </div>
                      <div>
                        <h5 className="font-medium text-sm">AI Diagnostics</h5>
                        <p className="text-sm text-gray-600">Machine learning for medical imaging and lab result analysis.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-green-100 p-2 rounded-md">
                        <BarChart3 className="h-5 w-5 text-green-700" />
                      </div>
                      <div>
                        <h5 className="font-medium text-sm">Predictive Analytics</h5>
                        <p className="text-sm text-gray-600">Forecasting patient flow and optimizing resource allocation.</p>
                      </div>
                    </div>
                    <div className="pt-4 mt-4 border-t border-gray-100 text-center">
                      <Badge className="bg-green-50 text-green-800 hover:bg-green-100">Integrates with hospital EHR systems</Badge>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
          {/* Clinics Tab */}
          <TabsContent value="clinics" className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="md:w-1/2">
                <Badge className="mb-3 bg-green-100 text-green-700 hover:bg-green-200">Clinics</Badge>
                <h3 className="text-2xl font-semibold mb-4">Remote Patient Monitoring & Chronic Care</h3>
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold mb-1">Industry Challenge</h4>
                    <p className="text-gray-600">Clinics must manage chronic disease patients, prevent readmissions, and coordinate care across multiple providers while ensuring high patient engagement.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Key Applications</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /><span>IoT-enabled remote patient monitoring for chronic conditions</span></li>
                      <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /><span>Automated medication adherence tracking and alerts</span></li>
                      <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /><span>Patient engagement and satisfaction analytics</span></li>
                      <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /><span>Interoperable health records for seamless care coordination</span></li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Expected Benefits</h4>
                    <p className="text-gray-600">Potential outcomes include 50-60% reduction in readmissions, 20-30% improvement in medication adherence, and higher patient satisfaction scores.</p>
                  </div>
                </div>
              </div>
              <div className="md:w-1/2 bg-gray-50 rounded-xl p-8">
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h4 className="font-medium text-lg mb-4 text-center">Technology Stack</h4>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="bg-green-100 p-2 rounded-md">
                        <HeartPulse className="h-5 w-5 text-green-700" />
                      </div>
                      <div>
                        <h5 className="font-medium text-sm">Remote Monitoring</h5>
                        <p className="text-sm text-gray-600">IoT devices and analytics for continuous patient monitoring.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-green-100 p-2 rounded-md">
                        <FileSearch className="h-5 w-5 text-green-700" />
                      </div>
                      <div>
                        <h5 className="font-medium text-sm">Interoperable Records</h5>
                        <p className="text-sm text-gray-600">Secure, compliant data exchange across providers.</p>
                      </div>
                    </div>
                    <div className="pt-4 mt-4 border-t border-gray-100 text-center">
                      <Badge className="bg-green-50 text-green-800 hover:bg-green-100">HIPAA & GDPR compliant</Badge>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
          {/* Research Tab */}
          <TabsContent value="research" className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="md:w-1/2">
                <Badge className="mb-3 bg-green-100 text-green-700 hover:bg-green-200">Research</Badge>
                <h3 className="text-2xl font-semibold mb-4">Precision Medicine & Clinical Trials</h3>
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold mb-1">Industry Challenge</h4>
                    <p className="text-gray-600">Research organizations need to accelerate drug discovery, improve clinical trial outcomes, and leverage genomic data for personalized therapies.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Key Applications</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /><span>Genomic analysis for targeted therapy development</span></li>
                      <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /><span>AI-powered patient matching for clinical trials</span></li>
                      <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /><span>Automated data collection and outcome analysis</span></li>
                      <li className="flex items-start gap-2 text-gray-600"><CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" /><span>Federated learning for privacy-preserving research</span></li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Expected Benefits</h4>
                    <p className="text-gray-600">Potential outcomes include 30-40% faster drug discovery, 40-50% improvement in trial enrollment, and more effective, personalized therapies.</p>
                  </div>
                </div>
              </div>
              <div className="md:w-1/2 bg-gray-50 rounded-xl p-8">
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <h4 className="font-medium text-lg mb-4 text-center">Technology Stack</h4>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="bg-green-100 p-2 rounded-md">
                        <Microscope className="h-5 w-5 text-green-700" />
                      </div>
                      <div>
                        <h5 className="font-medium text-sm">Genomic Analysis</h5>
                        <p className="text-sm text-gray-600">Bioinformatics and sequencing for precision medicine.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="bg-green-100 p-2 rounded-md">
                        <Activity className="h-5 w-5 text-green-700" />
                      </div>
                      <div>
                        <h5 className="font-medium text-sm">Federated Learning</h5>
                        <p className="text-sm text-gray-600">Privacy-preserving AI for multi-institutional research.</p>
                      </div>
                    </div>
                    <div className="pt-4 mt-4 border-t border-gray-100 text-center">
                      <Badge className="bg-green-50 text-green-800 hover:bg-green-100">Supports global research standards</Badge>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </motion.section>

      {/* Technology Stack */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={fadeIn}
        className="mb-20"
      >
        <h2 className="text-3xl font-bold mb-6 text-center">Technology Stack</h2>
        <p className="text-center text-gray-600 max-w-3xl mx-auto mb-12">
          Our HealthTech solutions leverage cutting-edge technologies to deliver secure, compliant, and innovative healthcare capabilities.
        </p>
        
        <div className="grid md:grid-cols-3 gap-6">
          {technologies.map((tech) => (
            <Card key={tech.name} className="bg-white">
              <CardHeader>
                <CardTitle>{tech.name}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-600">
                  {tech.description}
                </CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={fadeIn}
        className="bg-gradient-to-r from-teal-500 to-green-500 text-white rounded-2xl p-12 text-center"
      >
        <h2 className="text-3xl font-bold mb-4">Ready to Transform Healthcare Delivery?</h2>
        <p className="max-w-2xl mx-auto mb-8">
          Schedule a personalized demo to see how our HealthTech solutions can improve clinical outcomes, enhance operational efficiency, and revolutionize patient care for your organization.
        </p>
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <Button size="lg" variant="default" className="bg-white text-teal-600 hover:bg-gray-100">
            Request Demo
          </Button>
          <Button size="lg" variant="outline" className="text-white border-white hover:bg-white/10">
            Talk to Sales
          </Button>
        </div>
      </motion.section>

      {/* FAQ Section */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={fadeIn}
        className="mb-20"
      >
        <h2 className="text-3xl font-bold mb-12 text-center">Frequently Asked Questions</h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>How do you ensure compliance with healthcare regulations?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                All our solutions are designed with regulatory compliance as a foundation. We maintain dedicated compliance teams specializing in HIPAA, GDPR, FDA regulations, and other relevant standards. Our systems incorporate privacy-by-design principles, role-based access controls, comprehensive audit trails, and end-to-end encryption to ensure data security and regulatory adherence.
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Can your solutions integrate with our existing systems?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                Yes, our HealthTech solutions are built with interoperability as a core principle. We support integration with all major EHR/EMR systems, PACS, laboratory information systems, and practice management software through FHIR, HL7, DICOM, and other healthcare data standards. Our team includes integration specialists with experience across the healthcare IT ecosystem.
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>What clinical validation do your solutions have?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                Our solutions undergo rigorous clinical validation through partnerships with leading academic medical centers and research institutions. We conduct clinical studies following appropriate methodologies, with results published in peer-reviewed journals. For diagnostic tools, we perform extensive validation against expert consensus and ground truth, with clear documentation of sensitivity, specificity, and performance characteristics.
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>How do you support implementation and user adoption?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                We provide comprehensive implementation services tailored to healthcare environments, including workflow analysis, system configuration, data migration, and integration support. Our clinical adoption team works directly with practitioners to ensure solutions fit seamlessly into clinical workflows. We offer role-based training programs, 24/7 support, and ongoing optimization services to maximize the value of our solutions.
              </p>
            </CardContent>
          </Card>
        </div>
      </motion.section>
    </div>
  );
} 